import React, { useEffect, useState } from 'react';

import Table from '@/components/dataDisplay/Table';
import theme from '@/constants/theme';
import { routesService } from '@/services/routes';
import { UserRouteProgress } from '@/services/routes/route.vm';
import { RouteProgressStatus } from '@/services/routes/types';

const StatusChip = ({ status }: { status: RouteProgressStatus }) => {
  const chipConfig: Record<RouteProgressStatus, React.CSSProperties> = {
    NOT_STARTED: {
      backgroundColor: '#6e757d90',
      color: theme.colors.text.caption,
    },
    IN_PROGRESS: {
      backgroundColor: '#f5c44490',
      color: theme.colors.text.title,
    },
    COMPLETED: {
      backgroundColor: '#53a55290',
      color: theme.colors.text.title,
    },
  };

  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 8px',
        borderRadius: 4,
        width: 'fit-content',
        ...chipConfig[status],
        fontSize: 12,
      }}
    >
      {status}
    </span>
  );
};

interface Props {
  routeId?: string;
}

const UsersProgresses: React.FC<Props> = ({ routeId }) => {
  const [usersProgresses, setUsersProgresses] = useState<UserRouteProgress[] | null>(null);

  const mapUsersProgresses = (contents: UserRouteProgress[]) => {
    if (!contents) return [];
    return contents?.map((route) => ({
      id: route.id,
      userFullName: route.user,
      email: route.email,
      progress: route.progress,
      status: route.status,
      lastVisitedAt: route.lastVisitedAt,
    }));
  };

  useEffect(() => {
    if (!routeId) return;
    routesService.getUsersProgressesByRoute(routeId).then(setUsersProgresses);
  }, [routeId]);

  if (!routeId) return null;
  if (!usersProgresses || usersProgresses.length === 0)
    return (
      <p style={{ color: theme.colors.text.caption }}>Esta ruta aún no tiene interacciones de usuarios.</p>
    );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <h2 style={{ color: theme.colors.text.title }}>Progreso de usuarios</h2>
        <span style={{ color: theme.colors.text.caption }}>{usersProgresses.length} usuarios</span>
      </div>
      <Table
        columns={[
          {
            title: 'Usuario',
            field: 'userFullName',
            render: (row: any) => row.userFullName,
          },
          {
            title: 'Correo',
            field: 'email',
            render: (row: any) => row.email,
          },
          {
            title: 'Progreso',
            field: 'progress',
            render: (row: any) => `${Math.floor(row.progress ?? 0)}%`,
          },
          {
            title: 'Última visita',
            field: 'lastVisitedAt',
            render: (row: any) =>
              new Date(row.lastVisitedAt).toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              }),
          },
          {
            title: 'Estado',
            field: 'status',
            render: (row: any) => <StatusChip status={row.status} />,
          },
        ]}
        data={mapUsersProgresses(usersProgresses)}
      />
    </div>
  );
};

export default UsersProgresses;
